import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import { numberOr } from '../utils';
import { firstPostLikes } from '../utils/likes';

/**
 * Sem firstPost/lastPost: resumo, capa, curtidas e a última resposta do card vêm
 * em atributos da discussão (Api\DiscussionFields / DiscussionLikeFields).
 * Incluir os posts fazia o servidor renderizar dois posts inteiros por card.
 */
const SHOWCASE_INCLUDE = 'user,lastPostedUser,tags';

/** Teto do request de complemento da lista (ver `fillFeed`). */
const FILL_LIMIT = 50;

/** Espelham o default e o teto de `avocado.home_feed_count` (extend.php / admin). */
const DEFAULT_FEED_COUNT = 5;
const MAX_FEED_COUNT = 20;

/** Tamanho da página que a index entrega no boot (padrão do Flarum). */
const INDEX_PAGE_SIZE = 20;
/**
 * State for the Avocado home page.
 *
 * Owns:
 *  - The cached home feed (popular or latest, admin-chosen) and "topCategories".
 *  - The async showcase-discussions fetch and its per-slug cache.
 *  - The showcase tag-ID parsing from `app.forum.attribute('avocadoShowcaseTag')`.
 *
 * The page itself only reads these getters; it never calls `app.store` directly.
 */
export default class HomeState {
  constructor() {
    _defineProperty(this, "homeLoading", true);
    _defineProperty(this, "showcaseLoading", false);
    _defineProperty(this, "showcaseItems", []);
    _defineProperty(this, "showcaseFetched", false);
    _defineProperty(this, "showcaseCache", {});
    /** Complemento da lista quando a página do boot não cobre a contagem (ver `fillFeed`). */
    _defineProperty(this, "feedExtra", []);
    _defineProperty(this, "feedFill", null);
    /** Preload da lista de tags em voo (dedup entre a home e o showcase). */
    _defineProperty(this, "tagListLoad", null);
    // Memoization — invalidated when the store discussion count changes.
    _defineProperty(this, "cachedFeed", null);
    _defineProperty(this, "cachedStoreSize", -1);
  }
  // ── Read-only getters ──────────────────────────────────────────────────

  /** All discussions currently in the store, robust to paged-vs-flat shape. */
  allDiscussions() {
    try {
      const pages = app.discussions?.getPages?.();
      if (Array.isArray(pages) && pages.length > 0) {
        const out = [];
        if (typeof pages[0] === 'object' && pages[0] !== null && 'items' in pages[0]) {
          pages.forEach(p => p?.items && out.push(...p.items));
        } else {
          out.push(...pages);
        }
        const filtered = out.filter(Boolean);
        if (filtered.length > 0) return filtered;
      }
      return app.store.all('discussions').filter(Boolean);
    } catch {
      return app.store.all('discussions').filter(Boolean);
    }
  }

  /**
   * Popularity score: weighted sum of replies, likes, views, with a recency
   * bonus that decays linearly across one week.
   */
  discussionScore(d) {
    const replyCount = numberOr(d.replyCount?.(), 0);
    const likeCount = firstPostLikes(d).count;
    const views = numberOr(d.attribute?.('viewCount'), 0);
    const lastPostedAt = d.lastPostedAt?.();
    const ageMs = lastPostedAt ? Date.now() - new Date(lastPostedAt).getTime() : Infinity;
    const recency = Math.max(0, 1 - ageMs / (7 * 24 * 3600 * 1000));
    return replyCount * 2 + likeCount * 3 + views * 0.1 + recency * 20;
  }

  /** IDs of tags configured as the showcase source. */
  showcaseTagIds() {
    if (!app.forum?.attribute('avocadoShowcaseEnabled')) return new Set();
    const raw = app.forum?.attribute('avocadoShowcaseTag') || '';
    if (!raw) return new Set();
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return new Set(parsed.map(String).filter(Boolean));
    } catch {
      /* fallthrough */
    }
    const s = String(raw).trim();
    return s ? new Set([s]) : new Set();
  }
  isShowcaseDiscussion(d) {
    const ids = this.showcaseTagIds();
    if (!ids.size) return false;
    return (d.tags?.() || []).some(t => ids.has(String(t?.id?.())));
  }

  /** Ordem da lista da home (admin): `popular` (pontuação) ou `latest` (última atividade). */
  feedSort() {
    return app.forum?.attribute('avocadoHomeFeedSort') === 'latest' ? 'latest' : 'popular';
  }

  /** Quantas discussões a lista da home mostra (admin: 5 a 20). */
  feedCount() {
    const n = Math.round(numberOr(Number(app.forum?.attribute('avocadoHomeFeedCount')), DEFAULT_FEED_COUNT));
    return Math.min(MAX_FEED_COUNT, Math.max(1, n));
  }

  /** Discussions for the home rail, in the admin-chosen order and count. */
  feedDiscussions() {
    this.invalidateIfStoreChanged();
    if (this.cachedFeed?.length) return this.cachedFeed;
    const sort = this.feedSort();
    const lastPosted = d => {
      const at = d.lastPostedAt?.();
      return at ? new Date(at).getTime() : 0;
    };
    const result = this.feedCandidates().sort((a, b) => {
      const aSticky = a.isSticky?.() ? 1 : 0;
      const bSticky = b.isSticky?.() ? 1 : 0;
      if (bSticky !== aSticky) return bSticky - aSticky;
      return sort === 'latest' ? lastPosted(b) - lastPosted(a) : this.discussionScore(b) - this.discussionScore(a);
    }).slice(0, this.feedCount());
    if (result.length > 0) this.cachedFeed = result;
    return result;
  }

  /** O que pode entrar na lista: a página da index + o complemento de `fillFeed`, sem o showcase. */
  feedCandidates() {
    const seen = new Set();
    return [...this.allDiscussions(), ...this.feedExtra].filter(d => {
      const id = String(d?.id?.() ?? '');
      if (!id || seen.has(id) || this.isShowcaseDiscussion(d)) return false;
      seen.add(id);
      return true;
    });
  }

  /** Top-level tags sorted by their admin-defined position. */
  topCategories(limit) {
    if (limit === void 0) {
      limit = 7;
    }
    try {
      const tags = app.store.all('tags').filter(t => t && !t.parent?.());
      return tags.sort((a, b) => (a.position?.() ?? 9999) - (b.position?.() ?? 9999)).slice(0, limit);
    } catch {
      return [];
    }
  }

  /** Showcase discussions (cached, asynchronously populated). */
  showcase() {
    return this.showcaseItems;
  }

  // ── Mutating actions ───────────────────────────────────────────────────

  /** Drop the memoized feed when the store grows or shrinks. */
  invalidate() {
    this.cachedFeed = null;
  }
  invalidateIfStoreChanged() {
    const current = app.store.all('discussions').length;
    if (current !== this.cachedStoreSize) {
      this.cachedFeed = null;
      this.cachedStoreSize = current;
    }
  }

  /**
   * Preload da lista de tags, memoizado.
   *
   * A home usa para as categorias e o showcase para resolver id → slug; sem o
   * memo as duas disparariam GET /api/tags em paralelo no mesmo oninit.
   */
  loadTags() {
    if (this.tagListLoad) return this.tagListLoad;
    const tagList = app.tagList;
    const load = tagList?.load ? tagList.load(['children', 'parent']).catch(() => []) : Promise.resolve([]);
    this.tagListLoad = load;
    return load;
  }

  /** Fetch the home feed (deduplicates against existing store contents). */
  loadHome() {
    const existing = app.store.all('discussions');
    if (existing.length > 0) {
      // O store já está quente — na home isso é o caso NORMAL, não a exceção: o
      // apiDocument do boot da index traz 20 discussions com tags e
      // lastPostedUser. Como loadHome() roda no oninit, antes do primeiro
      // view(), desligar a flag aqui é síncrono e o 1º paint já sai com os
      // cards. A versão anterior segurava o skeleton por 350ms com um
      // setTimeout, o que produzia exatamente o flash que ele tentava evitar —
      // skeleton visível e depois substituído, com o dado pronto o tempo todo.
      this.homeLoading = false;

      // Esse payload não traz o firstPost (desde a série 2.0 RC o Index manda
      // só o *linkage*), e o card não precisa dele: descrição, capa, curtidas e
      // a última resposta vêm em atributos da discussão. O clique de curtir usa
      // o linkage (utils/likes). Nada a buscar depois do boot.
      this.feedFromBoot();
      return this.fillFeed();
    }
    this.invalidate();
    return app.store.find('discussions', {
      include: ['user', 'lastPostedUser', 'tags'],
      'page[limit]': 20
    }).then(() => {
      this.homeLoading = false;
      this.fillFeed();
      // Attempt to populate showcase from the newly-fetched store as a side-effect.
      if (this.showcaseLoading && !this.showcaseFetched) {
        const fromStore = this.showcaseFromStore();
        if (fromStore.length >= this.showcaseLimit()) {
          this.showcaseItems = fromStore;
          this.showcaseLoading = false;
          this.showcaseFetched = true;
        }
        // Parcial não pinta: o feed da home traz só a 1ª página, então a tag
        // pode ter mais discussões do que couberam nela. Marcar `fetched`
        // aqui truncava o trilho no que o store por acaso tivesse; pintar
        // sem marcar trocava a grade embaixo do usuário. O fetch por tag,
        // já em voo, dá a palavra final.
      }
      m.redraw();
    }).catch(() => {
      this.homeLoading = false;
      m.redraw();
    });
  }

  /**
   * Complemento da lista vindo do payload do boot (Content\PreloadHomeFeed).
   *
   * Roda no oninit, antes do 1º view(): pushPayload insere os models no store
   * sincronamente, então a lista já nasce com a contagem do admin e o
   * `fillFeed` logo em seguida não tem o que buscar.
   */
  feedFromBoot() {
    try {
      const payload = app.data?.avocadoHomeFeed;
      if (!payload?.data?.length || this.feedExtra.length) return;
      const pushed = app.store.pushPayload(payload);
      this.feedExtra = (Array.isArray(pushed) ? pushed : [pushed]).filter(Boolean);
      // Só vale para a 1ª entrada na home; voltar a ela usa o store.
      delete app.data.avocadoHomeFeed;
      this.invalidate();
    } catch {
      /* payload malformado — o fillFeed cobre */
    }
  }

  /**
   * Completa a lista quando a contagem do admin passa do que a página do boot
   * cobre: a index traz 20 discussões e as do showcase saem da conta, então
   * com 15 ou 20 itens a lista podia ficar curta. Um GET a mais, só nesse
   * caso; os itens novos entram no fim (ou na ordem da pontuação) depois do
   * 1º paint, que já sai com o que havia.
   */
  fillFeed() {
    if (this.feedFill) return this.feedFill;
    const count = this.feedCount();
    const available = this.feedCandidates().length;
    // Página do boot incompleta = o fórum não tem mais que isso; nada a buscar.
    if (available >= count || this.allDiscussions().length < INDEX_PAGE_SIZE) return Promise.resolve();
    const sort = this.feedSort() === 'latest' ? '-lastPostedAt' : '';
    const params = {
      include: SHOWCASE_INCLUDE,
      'page[limit]': Math.min(FILL_LIMIT, count + INDEX_PAGE_SIZE)
    };
    if (sort) params.sort = sort;
    this.feedFill = app.store.find('discussions', params).then(items => {
      this.feedExtra = Array.isArray(items) ? items.filter(Boolean) : [];
      this.invalidate();
      m.redraw();
    }).catch(() => {
      /* complemento é opcional — a lista fica com o que já tinha */
    });
    return this.feedFill;
  }

  /**
   * Fetch the showcase rail.
   *
   * Resolves synchronously when:
   *  - the showcase feature is disabled,
   *  - no showcase tag is configured,
   *  - o boot trouxe o trilho pronto (Content\PreloadShowcase — caminho
   *    normal, e o único sem skeleton), ou
   *  - the store already holds enough showcase items.
   *
   * Otherwise fetches each configured tag's slug + discussions in parallel
   * and merges the results into `this.showcaseItems`, mantendo o skeleton no
   * ar até lá — um resultado parcial nunca é pintado.
   */
  loadShowcase() {
    if (this.showcaseFetched || this.showcaseLoading) return Promise.resolve();
    if (!app.forum?.attribute('avocadoShowcaseEnabled')) {
      this.showcaseFetched = true;
      return Promise.resolve();
    }
    const raw = app.forum?.attribute('avocadoShowcaseTag');
    if (!raw) {
      this.showcaseFetched = true;
      return Promise.resolve();
    }
    let tagIds = [];
    try {
      const parsed = JSON.parse(raw);
      tagIds = (Array.isArray(parsed) ? parsed : [parsed]).map(String).filter(Boolean);
    } catch {
      const s = String(raw).trim();
      if (s) tagIds = [s];
    }
    if (!tagIds.length) {
      this.showcaseFetched = true;
      return Promise.resolve();
    }

    // Payload do boot: síncrono, o 1º paint já sai com os cards definitivos.
    const preloaded = this.showcaseFromBoot();
    if (preloaded.length) {
      this.showcaseItems = preloaded;
      this.showcaseFetched = true;
      return Promise.resolve();
    }
    this.showcaseLoading = true;
    const expectedCount = this.showcaseLimit();
    const fromStore = this.showcaseFromStore();
    if (fromStore.length >= expectedCount) {
      // Mesmo caso do loadHome(): os itens do showcase saem do store que o boot
      // já preencheu. Resolver síncrono — o skeleton do showcase nunca chega a
      // pintar quando o dado está pronto.
      this.showcaseItems = fromStore;
      this.showcaseLoading = false;
      this.showcaseFetched = true;
      return Promise.resolve();
    }

    // Store incompleto: ele só enxerga a primeira página do feed da home, e as
    // discussões da tag de showcase que ficaram fora dela simplesmente não
    // estão ali. Aceitar esse subconjunto como resultado final era o bug de
    // "a tag tem 3 discussões mas o trilho mostra 1".
    //
    // E o parcial também não pode ser PINTADO enquanto o fetch corre: a grade
    // sairia com 1 card ocupando a linha inteira e depois viraria 5 colunas,
    // que é exatamente o salto que o skeleton existe para evitar. Segura o
    // skeleton (dimensionado por avocadoShowcaseItemCount, que o servidor
    // calcula com a contagem real) até o resultado definitivo chegar.

    return Promise.all(tagIds.map(id => this.resolveTagSlug(id))).then(slugs => slugs.filter(Boolean)).then(slugs => {
      if (!slugs.length) {
        this.showcaseLoading = false;
        this.showcaseFetched = true;
        return [];
      }
      return Promise.all(slugs.map(slug => this.fetchShowcaseBySlug(slug)));
    }).then(batches => {
      if (this.showcaseFetched && this.showcaseItems.length) return;
      const seen = new Set();
      const limit = this.showcaseLimit();
      const merged = batches.flat().filter(Boolean).filter(d => {
        const id = d.id?.();
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      }).sort((a, b) => new Date(b.createdAt?.()) - new Date(a.createdAt?.())).slice(0, limit);
      this.showcaseItems = merged;
      this.showcaseLoading = false;
      this.showcaseFetched = true;
      m.redraw();
    }).catch(() => {
      this.showcaseLoading = false;
      this.showcaseFetched = true;
      m.redraw();
    });
  }

  /**
   * Showcase vindo do payload do boot (Content\PreloadShowcase).
   *
   * Roda dentro do oninit, antes do 1º view(): pushPayload insere os models no
   * store sincronamente, então não há skeleton nem re-render. A ordem do `data`
   * já vem do servidor (-createdAt) e é preservada.
   */
  showcaseFromBoot() {
    try {
      const payload = app.data?.avocadoShowcase;
      if (!payload?.data?.length) return [];
      const pushed = app.store.pushPayload(payload);
      const items = (Array.isArray(pushed) ? pushed : [pushed]).filter(Boolean);
      return items.slice(0, this.showcaseLimit());
    } catch {
      // Payload malformado nunca derruba a home — cai no caminho async.
      return [];
    }
  }

  /** Quantidade de cards configurada no admin (1–5, default 5). */
  showcaseLimit() {
    const n = parseInt(String(app.forum?.attribute('avocadoShowcaseCount') ?? ''), 10);
    return Number.isFinite(n) && n > 0 ? n : 5;
  }
  showcaseFromStore() {
    const ids = this.showcaseTagIds();
    if (!ids.size) return [];
    const limit = this.showcaseLimit();
    // `allDiscussions()` devolve as páginas do feed da home quando elas
    // existem; uma discussão do showcase que esteja no store mas fora do feed
    // (preload, navegação anterior) ficaria invisível. Aqui o universo certo é
    // o store inteiro.
    return [...app.store.all('discussions')].filter(Boolean).filter(d => this.isShowcaseDiscussion(d)).sort((a, b) => {
      const aSticky = a.isSticky?.() ? 1 : 0;
      const bSticky = b.isSticky?.() ? 1 : 0;
      if (bSticky !== aSticky) return bSticky - aSticky;
      return new Date(b.createdAt?.()) - new Date(a.createdAt?.());
    }).slice(0, limit);
  }
  resolveTagSlug(id) {
    const fromStore = () => {
      const tag = (app.store.all('tags') || []).find(t => String(t.id?.()) === id);
      return tag?.slug?.() || null;
    };
    const cached = fromStore();
    if (cached) return Promise.resolve(cached);

    // GET /api/tags/{id} não serve aqui: o slug driver padrão do flarum/tags
    // (Utf8SlugDriver) resolve a rota pela coluna `slug`, então um id numérico
    // devolve 404 e o trilho ficava vazio. A listagem é o caminho confiável.
    return this.loadTags().then(() => fromStore()).catch(() => null);
  }
  fetchShowcaseBySlug(slug) {
    if (!slug) return Promise.resolve([]);
    if (this.showcaseCache[slug]) return Promise.resolve(this.showcaseCache[slug]);
    return app.store.find('discussions', {
      filter: {
        tag: slug
      },
      include: SHOWCASE_INCLUDE,
      sort: '-createdAt',
      'page[limit]': this.showcaseLimit()
    }).then(results => {
      const filtered = Array.isArray(results) ? results.filter(Boolean) : [];
      this.showcaseCache[slug] = filtered;
      return filtered;
    }).catch(() => []);
  }
}
flarum.reg.add('ramon-avocado', 'forum/states/HomeState', HomeState);